
import React, { useRef, useEffect, useMemo } from 'react';
import { tokensToStructuredGrid, ChordValidation } from '../types';
import { useGuitarMatch } from '../context/GuitarMatchContext';

interface FretboardProps {
  progression: string[]; 
  activeMeasure: number; 
  onMeasureSelect: (index: number) => void;
  validation?: ChordValidation[];
  bpm?: number;
  beatsPerBar?: number;
}

interface FretboardBarProps {
    bar: string[];
    barIdx: number;
    isActive: boolean;
    validationItem?: ChordValidation;
    beatsPerBar: number;
    onSelect: (index: number) => void;
    fullProgression: string[][];
}

const FretboardBar = React.memo<FretboardBarProps>(({ 
    bar, barIdx, isActive, validationItem: v, beatsPerBar, onSelect, fullProgression
}) => {
    const { applyExercisePreset } = useGuitarMatch();
    const score = v?.score ?? null;
    
    const isCritical = score !== null && score < 45;
    const isWarning = score !== null && score >= 45 && score < 75;
    
    const scoreColor = isCritical ? 'text-red-500' : isWarning ? 'text-smart-gold' : 'text-emerald-400';
    const activeGlow = isCritical ? 'shadow-[0_0_40px_rgba(239,68,68,0.4)] border-red-500/50' : isWarning ? 'shadow-[0_0_40px_rgba(218,144,0,0.2)]' : 'shadow-[0_0_40px_rgba(16,185,129,0.2)]';

    const handlePracticeBar = (e: React.MouseEvent) => {
        e.stopPropagation();
        
        const prevBar = fullProgression[barIdx - 1];
        let drillGrid = "";
        let instructions = [];

        if (prevBar) {
            const leadIn = `${prevBar.slice(-2).join(' ')} | `;
            drillGrid = leadIn + bar.join(' ');
            instructions = [
                `Travaillez la transition vers cette mesure : ${drillGrid}`,
                "Objectif : Nettoyer chaque note de l'accord."
            ];
        } else {
            drillGrid = bar.join(' ');
            instructions = [
                `Focus Mesure 1 : ${drillGrid}`,
                "Vérifiez l'accordage avant de démarrer."
            ];
        }

        applyExercisePreset({
            id: `practice_bar_${barIdx}`,
            name: `Correction Mesure ${barIdx + 1}`,
            rules: [
                isCritical ? "Priorité : Position des doigts" : "Priorité : Franchise de l'attaque",
                ...instructions
            ],
            duration: "3 min",
            preset_grid: drillGrid,
            preset_level: 2
        });
    };

    return (
        <div 
            onClick={() => onSelect(barIdx)}
            className={`chord-bar-item flex flex-col items-center gap-8 transition-all duration-700 cursor-pointer min-w-[280px] px-10 py-10 relative border-l border-white/10 ${
                isActive ? `bg-white/[0.08] scale-105 z-10 ${activeGlow}` : 'opacity-40 hover:opacity-100 hover:bg-white/[0.02]'
            }`}
        >
            {isCritical && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 px-4 py-1 bg-red-600 text-white text-[8px] font-black uppercase tracking-widest rounded-full animate-bounce shadow-lg">
                    Accord Imprécis ⚠️
                </div>
            )}

            <div className="flex items-center gap-4 w-full justify-between relative z-10">
                <span className={`text-[11px] font-black uppercase tracking-widest ${isActive ? 'text-smart-gold' : 'text-slate-600'}`}>MESURE {barIdx + 1}</span>
                {score !== null && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-2xl border border-white/10 bg-black/60">
                        <div className={`w-2 h-2 rounded-full ${isCritical ? 'bg-red-500 shadow-[0_0_8px_red]' : 'bg-emerald-500'} animate-pulse`}></div>
                        <span className={`text-[12px] font-black italic ${scoreColor}`}>{score}%</span>
                    </div>
                )}
            </div>

            <div className={`w-full h-36 rounded-[48px] flex items-center justify-center p-8 relative border-2 transition-all duration-500 ${
                isActive ? (isCritical ? 'bg-white border-red-500' : 'bg-white border-white shadow-[0_0_50px_rgba(255,255,255,0.4)]') : 'bg-black/60 border-white/10'
            }`}>
                <span className={`text-5xl font-black italic tracking-tighter transition-colors ${isActive ? (isCritical ? 'text-red-600' : 'text-black') : 'text-white'}`}>{bar.join(' ')}</span>
                
                {isActive && (
                    <button 
                        onClick={handlePracticeBar}
                        className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center opacity-0 hover:opacity-100 transition-opacity rounded-[46px] gap-2"
                    >
                        <span className="bg-white text-black px-6 py-2 rounded-full font-black text-[10px] uppercase tracking-widest shadow-2xl">ISOLER CETTE ZONE ⚡</span>
                        <span className="text-[8px] text-slate-400 font-bold uppercase">Lancer un Drill de 3min</span>
                    </button>
                )}
            </div>
            
            {isActive && v?.detected && v.detected !== v.expected && (
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 z-20 px-3 py-1 bg-black border border-white/20 rounded-lg text-[9px] font-bold text-slate-400 italic shadow-xl">
                    Entendu : {v.detected}
                </div>
            )}
        </div>
    );
});

export const Fretboard: React.FC<FretboardProps> = ({ progression, activeMeasure, onMeasureSelect, validation = [] }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const structuredGrid = useMemo(() => tokensToStructuredGrid(progression), [progression]);

  useEffect(() => {
      if (scrollRef.current && activeMeasure >= 0) {
          const items = scrollRef.current.querySelectorAll('.chord-bar-item');
          const activeEl = items[activeMeasure] as HTMLElement;
          if (activeEl) activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
  }, [activeMeasure]);

  return (
    <div className="w-full bg-white/[0.02] p-8 md:p-14 rounded-[64px] border-2 border-white/10 shadow-3xl relative overflow-hidden backdrop-blur-3xl">
      <div className="flex justify-between items-center mb-10 px-6">
        <h3 className="text-3xl font-black text-white italic uppercase tracking-tighter flex items-center gap-4">
            <span className="text-smart-accent">🎯</span> Tracker <span className="text-smart-gold">Harmonique</span>
        </h3>
        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.5em] italic">Cliquez sur une mesure pour l'analyser</p>
      </div>

      <div ref={scrollRef} className="overflow-x-auto pb-8 pt-4 no-scrollbar w-full flex items-center">
        <div className="flex px-14">
            {structuredGrid.map((bar, barIdx) => (
                <FretboardBar key={barIdx} bar={bar} barIdx={barIdx} isActive={activeMeasure === barIdx} validationItem={validation.find(val => val.measureIndex === barIdx)} beatsPerBar={4} onSelect={onMeasureSelect} fullProgression={structuredGrid} />
            ))}
        </div>
      </div>
    </div>
  );
};
